export const SUP_PROFILE_KEYS = [
  "sup-touring",
  "sup-surf",
  "sup-downwind",
] as const;

export type SupProfileKey = (typeof SUP_PROFILE_KEYS)[number];
export type SupProfileConfidence = "full" | "partial" | "unavailable";

const SUP_PROFILE_SET = new Set<string>(SUP_PROFILE_KEYS);

const PRESENTATION: Record<SupProfileKey, {
  key: SupProfileKey;
  label: string;
  icon: string;
  isBeta: true;
}> = {
  "sup-touring": {
    key: "sup-touring",
    label: "Touring SUP",
    icon: "🛶",
    isBeta: true,
  },
  "sup-surf": {
    key: "sup-surf",
    label: "SUP Surf",
    icon: "🏄",
    isBeta: true,
  },
  "sup-downwind": {
    key: "sup-downwind",
    label: "Downwind SUP",
    icon: "🛶",
    isBeta: true,
  },
};

export type SupProfilePresentation = (typeof PRESENTATION)[SupProfileKey];

export function isSupProfileKey(value: string): value is SupProfileKey {
  return SUP_PROFILE_SET.has(value);
}

export function normaliseSupProfileKey(value: string): SupProfileKey | null {
  if (value === "sup") return "sup-touring";
  return isSupProfileKey(value) ? value : null;
}

/**
 * Normalise a public surface sport value without letting the client opt into
 * named profiles. The server-approved profile list is the capability boundary:
 * legacy keeps `sup`, while an approved public surface maps it one way to
 * Touring SUP and accepts only explicitly approved named keys.
 */
export function normaliseSupProfileSurfaceKey(
  value: string,
  allowedProfiles: readonly SupProfileKey[],
): string | null {
  const profile = normaliseSupProfileKey(value);
  if (profile === null) return value;
  if (allowedProfiles.length === 0) return value === "sup" ? "sup" : null;
  return allowedProfiles.includes(profile) ? profile : null;
}

export function sportPresentation(key: SupProfileKey): SupProfilePresentation {
  return PRESENTATION[key];
}

export function supProfileLabel(score: number): string {
  if (score >= 9) return "Hero";
  if (score >= 7.5) return "Firing";
  if (score >= 6.5) return "Great";
  if (score >= 5) return "Good";
  if (score >= 3.5) return "Fair";
  if (score >= 2) return "Poor";
  return "Flat";
}
